import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiArrowLeft,
  FiArrowRight,
  FiChevronLeft,
  FiChevronRight,
  FiCheck,
  FiCode,
  FiLayers,
  FiMaximize2,
  FiMessageCircle,
  FiPenTool,
  FiSearch,
  FiShield,
  FiSmartphone,
  FiTrendingUp,
  FiZap,
  FiX,
} from 'react-icons/fi';
import { api } from '../services/api';
import { useLocale } from '../context/LocaleContext';
import { hydrateService, resolveServiceMediaUrl } from '../utils/serviceContent';
import ServiceIcon from '../components/ServiceIcon';
import ServiceSeo from '../components/ServiceSeo';
import NotFound from './NotFound';
import './ServiceLanding.css';

const LANDING_THEMES = {
  'corporate-website-design': 'corporate',
  'ecommerce-website-design': 'commerce',
  'tourism-website-design': 'tourism',
  'medical-website-design': 'medical',
  'law-firm-website-design': 'legal',
  'hotel-website-design': 'hotel',
  'real-estate-website-design': 'estate',
  'trading-website-design': 'trading',
  'educational-website-design': 'education',
  'news-website-design': 'news',
  'beauty-website-design': 'beauty',
  'custom-website-design': 'custom',
};

const PROCESS_STEPS = [
  { key: 'discovery', icon: FiSearch },
  { key: 'experience', icon: FiPenTool },
  { key: 'development', icon: FiCode },
  { key: 'growth', icon: FiTrendingUp },
];

const ServiceLanding = () => {
  const { slug, landingSlug } = useParams();
  const { t } = useTranslation();
  const { locale, localePath, setPageAlternates } = useLocale();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeGalleryIndex, setActiveGalleryIndex] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);

    api.get(`/api/services/${encodeURIComponent(slug)}?locale=${encodeURIComponent(locale.code)}`)
      .then((response) => {
        if (cancelled || !response.success || !response.data) return;
        const hydrated = hydrateService(response.data);
        const landing = (hydrated.seoLandings || []).find(
          (item) => item?.isActive !== false && String(item.slug) === String(landingSlug)
        );
        if (!landing?.title) {
          setNotFound(true);
          setService(null);
          setPageAlternates(null);
          return;
        }

        setService(hydrated);
        setPageAlternates(Object.fromEntries(
          Object.entries(response.data.landingAlternates?.[landing.slug] || {}).map(([code, alternate]) => [
            code,
            `/${code}/services/${alternate.serviceSlug}/${alternate.landingSlug}`,
          ])
        ));
      })
      .catch(() => {
        if (!cancelled) {
          setNotFound(true);
          setService(null);
          setPageAlternates(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      setPageAlternates(null);
    };
  }, [landingSlug, locale.code, setPageAlternates, slug]);

  useEffect(() => {
    if (activeGalleryIndex === null) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setActiveGalleryIndex(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeGalleryIndex]);

  const landing = useMemo(
    () => (service?.seoLandings || []).find((item) => String(item.slug) === String(landingSlug)),
    [landingSlug, service]
  );
  const relatedLandings = useMemo(
    () => (service?.seoLandings || [])
      .filter((item) => item?.isActive !== false && item?.title && item.slug !== landingSlug)
      .slice(0, 3),
    [landingSlug, service]
  );

  if (loading) {
    return <main className="service-landing-page"><div className="service-landing-loading" role="status">{t('servicesPage.loading')}</div></main>;
  }
  if (notFound || !service || !landing) return <NotFound />;

  const contentParagraphs = String(landing.content || landing.description || '')
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
  const whyParagraphs = String(landing.whyContent || '')
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
  const gallery = (landing.gallery || []).filter((item) => item?.image);
  const highlightItems = landing.highlightDetails?.length
    ? landing.highlightDetails
    : (landing.highlights || []).map((title, index) => ({
      id: `${landing.slug}-highlight-${index}`,
      title,
      description: '',
      image: gallery[index]?.image || '',
    }));
  const lightboxItems = [
    ...gallery,
    ...highlightItems
      .filter((highlight) => highlight.image && !gallery.some((item) => item.image === highlight.image))
      .map((highlight) => ({ image: highlight.image, alt: highlight.title })),
  ];
  const processSteps = landing.processSteps?.length
    ? landing.processSteps
    : PROCESS_STEPS.map(({ key }) => ({
      id: key,
      title: t(`serviceUi.landings.process.${key}.title`),
      description: t(`serviceUi.landings.process.${key}.description`),
    }));
  const ctaLandingTitle = String(landing.title || '').replace(/^وب[\s‌-]*سایت\s*/u, 'سایت ');
  const theme = LANDING_THEMES[landing.slug] || 'custom';

  return (
    <main className="service-landing-page" data-landing-theme={theme}>
      <ServiceSeo service={service} landing={landing} />

      <div className="service-landing-shell">
        <Link className="service-landing-back" to={localePath(`/services/${service.slug}`)}>
          <FiArrowRight aria-hidden="true" />
          {t('serviceUi.landings.backToService', { service: service.title })}
        </Link>

        <section className="service-landing-hero">
          <div className="service-landing-hero-copy">
            <span className="service-landing-eyebrow">
              <ServiceIcon name={landing.icon} />
              {landing.subtitle || service.title}
            </span>
            <h1>{landing.title}</h1>
            <p>{landing.description}</p>
            <div className="service-landing-actions">
              <Link className="service-landing-primary-action" to={localePath('/contact')}>
                <FiMessageCircle aria-hidden="true" />
                {t('serviceUi.landings.consultation')}
              </Link>
              <Link className="service-landing-secondary-action" to={localePath(`/services/${service.slug}#pricing-plans`)}>
                <FiLayers aria-hidden="true" />
                {t('serviceUi.landings.viewPlans')}
              </Link>
            </div>
          </div>
          <div className="service-landing-hero-visual">
            <img src={resolveServiceMediaUrl(landing.image || service.image)} alt={landing.title} />
          </div>
        </section>

        <section className="service-landing-trust-rail" aria-label={t('serviceUi.landings.standards')}>
          <div><FiSmartphone aria-hidden="true" /><span><strong>{t('serviceUi.landings.mobileFirst')}</strong><small>{t('serviceUi.landings.mobileFirstHint')}</small></span></div>
          <div><FiZap aria-hidden="true" /><span><strong>{t('serviceUi.landings.performance')}</strong><small>{t('serviceUi.landings.performanceHint')}</small></span></div>
          <div><FiSearch aria-hidden="true" /><span><strong>{t('serviceUi.landings.seoReady')}</strong><small>{t('serviceUi.landings.seoReadyHint')}</small></span></div>
          <div><FiShield aria-hidden="true" /><span><strong>{t('serviceUi.landings.secure')}</strong><small>{t('serviceUi.landings.secureHint')}</small></span></div>
        </section>

        <section className="service-landing-process" aria-labelledby="service-landing-process-title">
          <header>
            <span>{t('serviceUi.landings.processKicker')}</span>
            <h2 id="service-landing-process-title">{landing.processTitle || t('serviceUi.landings.processTitle')}</h2>
            <p>{landing.processDescription || t('serviceUi.landings.processDescription')}</p>
          </header>
          <ol>
            {processSteps.map((step, index) => {
              const Icon = PROCESS_STEPS[index % PROCESS_STEPS.length].icon;
              return (
              <li key={step.id || `${landing.slug}-process-${index}`}>
                <span className="service-landing-process-index">{String(index + 1).padStart(2, '0')}</span>
                <span className="service-landing-process-icon"><Icon aria-hidden="true" /></span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </li>
              );
            })}
          </ol>
        </section>

        <section className="service-landing-story">
          <div className="service-landing-story-aside" aria-hidden="true">
            <ServiceIcon name={landing.icon} />
            <strong>{landing.title}</strong>
            <span>{landing.subtitle}</span>
          </div>
          <article className="service-landing-content">
            <span className="service-landing-section-label">{t('serviceUi.landings.about')}</span>
            <h2>{landing.contentTitle || t('serviceUi.landings.bestTitle', { landing: landing.title })}</h2>
            {contentParagraphs.map((paragraph, index) => <p key={`${landing.slug}-${index}`}>{paragraph}</p>)}
          </article>
        </section>

        {!!gallery.length && (
          <section className="service-landing-gallery" aria-labelledby="service-landing-gallery-title">
            <header>
              <div>
                <span>{t('serviceUi.landings.galleryKicker')}</span>
                <h2 id="service-landing-gallery-title">{t('serviceUi.landings.galleryTitle', { landing: landing.title })}</h2>
              </div>
              <p>{t('serviceUi.landings.galleryDescription')}</p>
            </header>
            <div className="service-landing-gallery-grid">
              {gallery.map((item, index) => (
                <button type="button" key={item.id || item.image} onClick={() => setActiveGalleryIndex(index)} aria-label={item.alt || t('serviceUi.landings.openGalleryImage', { index: index + 1 })}>
                  <img src={resolveServiceMediaUrl(item.image)} alt={item.alt || `${landing.title} ${index + 1}`} loading="lazy" />
                  <span><FiMaximize2 aria-hidden="true" />{String(index + 1).padStart(2, '0')}</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {!!whyParagraphs.length && (
          <section className="service-landing-why" aria-labelledby="service-landing-why-title">
            <div className="service-landing-why-heading">
              <span>{t('serviceUi.landings.whyKicker')}</span>
              <h2 id="service-landing-why-title">{landing.whyTitle || t('serviceUi.landings.whyTitle', { landing: landing.title })}</h2>
              <ServiceIcon name={landing.icon} aria-hidden="true" />
            </div>
            <div className="service-landing-why-content">
              {whyParagraphs.map((paragraph, index) => <p key={`${landing.slug}-why-${index}`}>{paragraph}</p>)}
            </div>
          </section>
        )}

        {!!highlightItems.length && (
          <section className="service-landing-value-section" aria-labelledby="service-landing-value-title">
            <header>
              <span>{t('serviceUi.landings.valueKicker')}</span>
              <h2 id="service-landing-value-title">{t('serviceUi.landings.valueTitle', { landing: landing.title })}</h2>
              <p>{t('serviceUi.landings.valueDescription')}</p>
            </header>
            <div className="service-landing-highlights">
              {highlightItems.map((highlight, index) => (
                <article key={highlight.id || highlight.title} className={highlight.image ? '' : 'is-text-only'}>
                  {!!highlight.image && (
                    <button
                      type="button"
                      className="service-landing-highlight-media"
                      onClick={() => setActiveGalleryIndex(lightboxItems.findIndex((item) => item.image === highlight.image))}
                      aria-label={t('serviceUi.landings.openHighlightImage', { title: highlight.title })}
                    >
                      <img src={resolveServiceMediaUrl(highlight.image)} alt={highlight.title} loading="lazy" />
                      <span className="service-landing-highlight-zoom"><FiMaximize2 aria-hidden="true" /></span>
                    </button>
                  )}
                  <div className="service-landing-highlight-copy">
                    <span>{String(index + 1).padStart(2, '0')}</span>
                    <FiCheck aria-hidden="true" />
                    <h3>{highlight.title}</h3>
                    {!!highlight.description && <p>{highlight.description}</p>}
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        <section className="service-landing-cta">
          <div>
            <span>{t('serviceUi.landings.ctaKicker')}</span>
            <h2>{t('serviceUi.landings.ctaTitle', { landing: ctaLandingTitle })}</h2>
            <p>{t('serviceUi.landings.ctaDescription')}</p>
          </div>
          <Link to={localePath('/contact')}>
            {t('serviceUi.landings.startProject')}
            <FiArrowLeft aria-hidden="true" />
          </Link>
        </section>

        {!!relatedLandings.length && (
          <section className="service-landing-related">
            <header>
              <span>{t('serviceUi.landings.relatedKicker')}</span>
              <h2>{t('serviceUi.landings.relatedTitle')}</h2>
            </header>
            <div>
              {relatedLandings.map((item) => (
                <Link key={item.slug} to={localePath(`/services/${service.slug}/${item.slug}`)}>
                  <ServiceIcon name={item.icon} aria-hidden="true" />
                  <span><strong>{item.title}</strong><small>{item.subtitle}</small></span>
                  <FiArrowLeft aria-hidden="true" />
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      {activeGalleryIndex !== null && lightboxItems[activeGalleryIndex] && (
        <div className="service-landing-lightbox" role="dialog" aria-modal="true" aria-label={lightboxItems[activeGalleryIndex].alt || landing.title} onClick={() => setActiveGalleryIndex(null)}>
          <button className="service-landing-lightbox-close" type="button" onClick={() => setActiveGalleryIndex(null)} aria-label={t('serviceUi.landings.closeGallery')}><FiX /></button>
          <button className="service-landing-lightbox-nav is-prev" type="button" onClick={(event) => { event.stopPropagation(); setActiveGalleryIndex((activeGalleryIndex - 1 + lightboxItems.length) % lightboxItems.length); }} aria-label={t('serviceUi.landings.previousImage')}><FiChevronRight /></button>
          <figure onClick={(event) => event.stopPropagation()}>
            <img src={resolveServiceMediaUrl(lightboxItems[activeGalleryIndex].image)} alt={lightboxItems[activeGalleryIndex].alt || landing.title} />
            <figcaption><span>{String(activeGalleryIndex + 1).padStart(2, '0')} / {String(lightboxItems.length).padStart(2, '0')}</span>{lightboxItems[activeGalleryIndex].alt}</figcaption>
          </figure>
          <button className="service-landing-lightbox-nav is-next" type="button" onClick={(event) => { event.stopPropagation(); setActiveGalleryIndex((activeGalleryIndex + 1) % lightboxItems.length); }} aria-label={t('serviceUi.landings.nextImage')}><FiChevronLeft /></button>
        </div>
      )}
    </main>
  );
};

export default ServiceLanding;
